import i18n from '@/i18n'
import { mcToast } from '@/plugins/McToastAlertPlugin'
import store from '@/state/store'
import moment from 'moment'
import Vue from 'vue'

export const state = {
  type: null,
  message: null,
  licenseNew: false,
  config: {
    time: {
      key: 10,
      label: '10 Detik',
    },
    alertCodes: [],
    alertSounds: [],
  },
  status: '',
  detailNotification: null,
  listNotificationMedia: [],
  dataDvhRequested: [],
  popupState: {
    alertCenterPopup: false,
    popupPosition: { x: 100, y: 100 },
  },
  activePopups: [],
  alertList: [],
  newAlertRules: false,
  alertRuleIds: {
    urgentAlertRules: [],
    importantAlertRules: [],
    autoPopupAlertRules: [],
  },
  alertRuleObjs: {},
  isAlertListLoaded: false,
  unresolvedAlertCounts: {
    urgent: 0,
    important: 0,
  },
}

function clampCount(value) {
  return Math.max(0, Number(value) || 0)
}

function isSameAlert(alert, id) {
  if (id == null) return false
  const alertId = alert?.id
  const rawAlertId = alert?.rawAlertId

  if (alertId == null && rawAlertId == null) return false

  return String(alertId) === String(id) || String(rawAlertId) === String(id)
}
function findPopup(state, id) {
  if (id == null) return undefined
  return state.activePopups.find(
    (popup) => String(popup.id) === String(id) || isSameAlert(popup.alert, id)
  )
}

function findAlert(state, id) {
  return state.alertList.find((alert) => isSameAlert(alert, id))
}

function getAlertCountType(alert) {
  if (alert?.isUrgent) return 'urgent'
  if (alert?.isImportant && !alert?.isUrgent) return 'important'
  return null
}

function isUnresolvedEligible(alert) {
  return Boolean(alert) && alert.isResolved !== true && alert.isFalseAlert !== true
}

function getAlertSnapshot(state, id) {
  const alert = findAlert(state, id) || findPopup(state, id)?.alert || state.detailNotification

  if (!isSameAlert(alert, id)) {
    return null
  }

  return { ...alert }
}

function applyUnresolvedCountTransition(state, beforeAlert, afterAlert) {
  const beforeType = getAlertCountType(beforeAlert)
  const afterType = getAlertCountType(afterAlert)
  const wasEligible = isUnresolvedEligible(beforeAlert)
  const isEligible = isUnresolvedEligible(afterAlert)

  if (wasEligible && beforeType && (!isEligible || beforeType !== afterType)) {
    state.unresolvedAlertCounts = {
      ...state.unresolvedAlertCounts,
      [beforeType]: clampCount(state.unresolvedAlertCounts[beforeType] - 1),
    }
  }

  if (isEligible && afterType && (!wasEligible || beforeType !== afterType)) {
    state.unresolvedAlertCounts = {
      ...state.unresolvedAlertCounts,
      [afterType]: clampCount(state.unresolvedAlertCounts[afterType] + 1),
    }
  }
}

function flattenUserLogs(userLogs = []) {
  if (userLogs.length > 0 && userLogs[0]?.logs) {
    return userLogs.flatMap((group) => group.logs)
  }

  return [...userLogs]
}

function groupUserLogs(userLogs) {
  const grouped = userLogs.reduce((groups, logItem) => {
    const date = moment(logItem.createdOn).format('DD MMM YYYY')
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(logItem)
    return groups
  }, {})

  return Object.entries(grouped)
    .map(([date, logs]) => ({
      date,
      logs: logs.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)),
    }))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
}

function syncDetailNotification(state, id, patch) {
  if (!isSameAlert(state.detailNotification, id)) return

  state.detailNotification = {
    ...state.detailNotification,
    ...patch,
  }
}

function getPopupInitialPosition(alertRuleIds = null) {
  const fallbackPosition = {
    x: Math.floor(Math.random() * 300),
    y: Math.floor(Math.random() * 300),
  }

  const hasCdmMonitoringAccess = store.getters['identity/hasAccess'](['cdm-monitoring:read'])

  if (!hasCdmMonitoringAccess) {
    return fallbackPosition
  }

  const featureFlagValue = store.getters['identity/getFeatureFlagValue']('customFeature.brinks')
  const companyId = store.state.identity.whoami?.companyId

  if (!featureFlagValue || !companyId) {
    return fallbackPosition
  }

  if (
    !Array.isArray(featureFlagValue?.company_id) ||
    !featureFlagValue.company_id.includes(companyId) ||
    !featureFlagValue.custom_alert_position?.enable
  ) {
    return fallbackPosition
  }

  const customPositionAlertRuleId = featureFlagValue.custom_alert_position.alert_rule_id

  if (
    !Array.isArray(alertRuleIds) ||
    !Array.isArray(customPositionAlertRuleId) ||
    !Array.isArray(alertRuleIds) ||
    !customPositionAlertRuleId.some((id) => alertRuleIds.includes(id))
  ) {
    return fallbackPosition
  }

  const popupWidth = 400
  const minX = Math.floor(window.innerWidth * 0.5)
  const maxX = Math.max(window.innerWidth - popupWidth, 0)
  const safeMinX = Math.min(minX, maxX)
  const randomizedX = safeMinX + Math.floor(Math.random() * (Math.max(maxX - safeMinX, 0) + 1))

  return {
    ...fallbackPosition,
    x: randomizedX,
  }
}

export const mutations = {
  SET_STATUS(state, payload) {
    state.status = payload
  },
  success(state, message) {
    state.type = 'alert-success'
    state.message = message
  },
  error(state, message) {
    state.type = 'alert-danger'
    state.message = message
  },
  clear(state) {
    state.type = null
    state.message = null
  },
  configPopUp(state, payload) {
    state.config = payload
  },
  setDetailNotification(state, payload) {
    state.detailNotification = payload
  },
  setListNotificationMedia(state, payload) {
    state.listNotificationMedia = payload
  },
  updateDataListNotificationMedia(state, { downloadId, data }) {
    const temp = [...state.listNotificationMedia]
    const index = temp.findIndex((item) => item.downloadId === downloadId)
    if (index !== -1) {
      temp[index] = { ...temp[index], ...data }
      state.listNotificationMedia = temp
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Download ID ${downloadId} not found in listNotificationMedia`)
    }
  },
  addDataDvhRequested(state, payload) {
    state.dataDvhRequested = [...state.dataDvhRequested, payload]
  },
  updateDataDvhRequested(state, { downloadId, data }) {
    const temp = [...state.dataDvhRequested]
    const index = temp.findIndex((item) => item.downloadId === downloadId)
    if (index !== -1) {
      temp[index] = { ...temp[index], ...data }
      state.dataDvhRequested = temp
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Download ID ${downloadId} not found in dataDvhRequested`)
    }
  },
  resetDataDvhRequested(state) {
    state.dataDvhRequested = []
  },
  setPopupState(state, newState) {
    state.popupState = { ...state.popupState, ...newState }
  },
  ADD_POPUP(state, popup) {
    state.activePopups.push(popup)
    localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
  },
  REMOVE_POPUP(state, alertId) {
    const filteredData = state.activePopups.filter(
      (popup) => String(popup.id) !== String(alertId) && !isSameAlert(popup.alert, alertId)
    )
    state.activePopups = filteredData
    localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
  },
  UPDATE_POPUP_POSITION(state, { id, x, y }) {
    const popup = findPopup(state, id)
    if (popup) {
      popup.x = x
      popup.y = y
      localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
    }
  },
  UPDATE_POPUP_MEDIA(
    state,
    { id, pollingStatus, canCheck, canRequest, retryCount, videoData, isOfflineDevice }
  ) {
    const popup = findPopup(state, id)
    const alertId = popup?.alert?.rawAlertId ?? id
    const patch = {}

    if (pollingStatus !== undefined) patch.pollingStatus = pollingStatus
    if (videoData !== undefined) patch.videoData = videoData
    if (canCheck !== undefined) patch.canCheck = canCheck
    if (canRequest !== undefined) patch.canRequest = canRequest
    if (retryCount !== undefined) patch.retryCount = retryCount
    if (isOfflineDevice !== undefined) patch.isOfflineDevice = isOfflineDevice

    if (popup) {
      Object.assign(popup.alert, patch)
      localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
    }

    const alert = findAlert(state, alertId)
    if (alert) {
      Object.assign(alert, patch)
    }

    syncDetailNotification(state, alertId, patch)
  },
  LOAD_POPUPS(state, popups) {
    state.activePopups = popups
  },
  OPEN_POPUP_FORM(state, { id, formOpen }) {
    const popup = findPopup(state, id)
    if (popup) {
      popup.formOpen = formOpen
      localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
    }
  },
  UPDATE_POPUP_FORM(state, { id, formData }) {
    const popup = findPopup(state, id)
    if (popup) {
      popup.form = formData
      localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
    }
  },
  UPDATE_POPUP_FALSE_ALERT(state, { id, lastUserFeedback, isFalseAlert }) {
    const beforeAlert = getAlertSnapshot(state, id)
    const popup = findPopup(state, id)
    const alertId = popup?.alert?.rawAlertId ?? id
    if (popup) {
      popup.alert.lastUserFeedback = lastUserFeedback
      popup.alert.isFalseAlert = isFalseAlert
      localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
    }
    const alert = findAlert(state, alertId)
    if (alert) {
      alert.lastUserFeedback = lastUserFeedback
      alert.isFalseAlert = isFalseAlert
    }
    syncDetailNotification(state, alertId, {
      lastUserFeedback,
      isFalseAlert,
    })
    applyUnresolvedCountTransition(state, beforeAlert, {
      ...(beforeAlert || popup?.alert || findAlert(state, alertId)),
      isFalseAlert,
    })
  },

  // if there will be changes on this function regarding code on the "helper" please also update on helper function groupLogsByDate
  ADD_USER_LOG(state, { id, log }) {
    const popup = findPopup(state, id)
    const alertId = popup?.alert?.rawAlertId ?? id
    const detailNotification = isSameAlert(state.detailNotification, alertId)
      ? state.detailNotification
      : null
    const alert = findAlert(state, alertId)
    const sourceLogs =
      popup?.alert?.userLogs || detailNotification?.userLogs || alert?.userLogs || []
    const sortedLogs = groupUserLogs([...flattenUserLogs(sourceLogs), log])

    if (popup) {
      Vue.set(popup.alert, 'userLogs', sortedLogs)
      localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
    }

    if (alert) {
      Vue.set(alert, 'userLogs', sortedLogs)
    }

    if (detailNotification) {
      state.detailNotification = {
        ...state.detailNotification,
        userLogs: sortedLogs,
      }
    }
  },

  SET_ALERT_LIST(state, alerts) {
    state.alertList = alerts
  },
  UPDATE_NOTE_ONLY(state, { id, note, disabledNote }) {
    const popup = findPopup(state, id)
    if (popup) {
      popup.note = note
      popup.disabledNote = disabledNote
    }
  },
  UPDATE_FALSE_ALERT(state, { id, lastUserFeedback, isFalseAlert }) {
    const beforeAlert = getAlertSnapshot(state, id)
    const popup = findPopup(state, id)
    const alertId = popup?.alert?.rawAlertId ?? id
    if (popup) {
      popup.alert.lastUserFeedback = lastUserFeedback
      popup.alert.isFalseAlert = isFalseAlert
    }
    syncDetailNotification(state, alertId, {
      lastUserFeedback,
      isFalseAlert,
    })
    applyUnresolvedCountTransition(state, beforeAlert, {
      ...(beforeAlert || popup?.alert || findAlert(state, alertId)),
      isFalseAlert,
    })
  },
  RESET_NOTE_STATE(state, { id }) {
    const popup = findPopup(state, id)
    if (popup) {
      popup.note = ''
      popup.disabledNote = false

      if (popup.alert.lastUserFeedback) {
        popup.alert.lastUserFeedback = ''
      }
    }
    const alert = findAlert(state, id)
    if (alert) {
      alert.lastUserFeedback = ''
    }
  },
  UPDATE_FALSE_ALERT_ONLY(state, { id, lastUserFeedback }) {
    const popup = findPopup(state, id)
    const alertId = popup?.alert?.rawAlertId ?? id
    if (popup) {
      // Update status false alert tapi pertahankan note dan form
      popup.alert.lastUserFeedback = lastUserFeedback
    }
    syncDetailNotification(state, alertId, {
      lastUserFeedback,
    })
  },
  UPDATE_POPUP_NOTE(state, { id, note }) {
    const popup = findPopup(state, id)
    if (popup) {
      popup.note = note
      localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
    }
  },
  UPDATE_POPUP_RESOLVED(state, { id, isResolved }) {
    const beforeAlert = getAlertSnapshot(state, id)
    const popup = findPopup(state, id)
    const alertId = popup?.alert?.rawAlertId ?? id
    if (popup) {
      popup.alert.isResolved = isResolved

      localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
    }
    const alert = findAlert(state, alertId)

    if (alert) {
      alert.isResolved = isResolved
    }
    syncDetailNotification(state, alertId, {
      isResolved,
    })
    applyUnresolvedCountTransition(state, beforeAlert, {
      ...(beforeAlert || popup?.alert || findAlert(state, alertId)),
      isResolved,
    })
  },
  UPDATE_ALERT_LIST_RESOLVED(state, { id, isResolved }) {
    const beforeAlert = getAlertSnapshot(state, id)
    const alert = findAlert(state, id)
    if (alert) {
      alert.isResolved = isResolved
    }
    syncDetailNotification(state, id, {
      isResolved,
    })
    applyUnresolvedCountTransition(state, beforeAlert, {
      ...(beforeAlert || alert || state.detailNotification),
      isResolved,
    })
  },
  UPDATE_POPUP_RESOLVING(state, { id, isResolving }) {
    const popup = findPopup(state, id)
    if (popup) {
      popup.isResolving = isResolving
      localStorage.setItem('activePopups', JSON.stringify(state.activePopups))
    }
  },
  SET_NEW_ALERT_RULES(state, value) {
    state.newAlertRules = value
  },

  SET_ALERT_RULE_IDS(state, alertRuleIds) {
    state.alertRuleIds = alertRuleIds
  },

  SET_ALERT_RULES_OBJ(state, alertRuleObjs) {
    state.alertRuleObjs = alertRuleObjs
  },
  SET_ALERT_LIST_LOADED(state, isLoaded) {
    state.isAlertListLoaded = isLoaded
  },
  SET_UNRESOLVED_ALERT_COUNTS(state, counts = {}) {
    state.unresolvedAlertCounts = {
      urgent: clampCount(counts.urgent),
      important: clampCount(counts.important),
    }
  },
  ADJUST_UNRESOLVED_ALERT_COUNT(state, { type, delta = 0 } = {}) {
    if (type !== 'urgent' && type !== 'important') return

    state.unresolvedAlertCounts = {
      ...state.unresolvedAlertCounts,
      [type]: clampCount(state.unresolvedAlertCounts[type] + delta),
    }
  },
}

export const actions = {
  success({ commit }, message) {
    commit('success', message)
  },
  error({ commit }, message) {
    commit('error', message)
  },
  clear({ commit }) {
    commit('clear')
  },
  addPopup({ commit, state }, { alert, form, formOpen, lastOpenedAt }) {
    const alertId = alert.rawAlertId ?? alert.id
    const isExists = state.activePopups.some((popup) => isSameAlert(popup.alert, alertId))
    if (isExists) return
    if (
      !store.getters['identity/hasAccess'](['e4f-auto-popup-urgent-alert:read']) &&
      state.activePopups.length >= 9
    ) {
      mcToast.warning(i18n.t('notification.text.limitNotification'), { timeout: 1000 })
      return
    }

    const existingAlert = findAlert(state, alertId)
    const userLogs = existingAlert?.userLogs || alert.userLogs || []
    const popupPosition = getPopupInitialPosition(alert?.alertRuleIds)

    const newPopup = {
      id: alert.id,
      alert: {
        ...alert,
        userLogs: userLogs,
      },
      form,
      formOpen,
      lastOpenedAt,
      note: '',
      disabledNote: alert.lastUserFeedback ? true : false,
      isResolving: false,
      x: popupPosition.x,
      y: popupPosition.y,
    }
    commit('ADD_POPUP', newPopup)
  },
  closePopup({ commit }, alertId) {
    commit('REMOVE_POPUP', alertId)
  },
  updatePosition({ commit }, { id, x, y }) {
    commit('UPDATE_POPUP_POSITION', { id, x, y })
  },
  updateMedia(
    { commit },
    { id, pollingStatus, canCheck, canRequest, retryCount, videoData, isOfflineDevice }
  ) {
    commit('UPDATE_POPUP_MEDIA', {
      id,
      pollingStatus,
      canCheck,
      canRequest,
      retryCount,
      videoData,
      isOfflineDevice,
    })
  },
  setMediaPollingState(
    { dispatch },
    {
      id,
      pollingStatus,
      canCheck = false,
      canRequest = false,
      retryCount = 0,
      isOfflineDevice = false,
      ...overrides
    }
  ) {
    const payload = {
      id,
      pollingStatus,
      canCheck,
      canRequest,
      retryCount,
      isOfflineDevice,
      ...overrides,
    }

    if (!Object.prototype.hasOwnProperty.call(overrides, 'videoData')) {
      delete payload.videoData
    }

    return dispatch('updateMedia', payload)
  },
  openPopupForm({ commit }, { id, formOpen }) {
    commit('OPEN_POPUP_FORM', { id, formOpen })
  },
  updatePopupForm({ commit }, { id, formData }) {
    commit('UPDATE_POPUP_FORM', { id, formData })
  },
  updatePopupFalseAlert({ commit }, { id, lastUserFeedback, isFalseAlert }) {
    commit('UPDATE_POPUP_FALSE_ALERT', { id, lastUserFeedback, isFalseAlert })
  },
  updatePopupNote({ commit }, { id, note }) {
    commit('UPDATE_POPUP_NOTE', { id, note })
  },
  addUserLog({ commit }, { id, log }) {
    commit('ADD_USER_LOG', { id, log })
  },

  updateFalseAlert({ commit }, payload) {
    commit('UPDATE_FALSE_ALERT', payload)
  },

  resetNoteState({ commit }, payload) {
    commit('RESET_NOTE_STATE', payload)
  },

  updatePopupResolved({ commit }, { id, isResolved }) {
    commit('UPDATE_POPUP_RESOLVED', { id, isResolved })
  },

  updatePopupResolving({ commit }, { id, isResolving }) {
    commit('UPDATE_POPUP_RESOLVING', { id, isResolving })
  },

  showNewAlertRulesToast({ commit }) {
    commit('SET_NEW_ALERT_RULES', true)
  },

  clearNewAlertRulesToast({ commit }) {
    commit('SET_NEW_ALERT_RULES', false)
  },

  updateAlertListResolved({ commit }, { id, isResolved }) {
    commit('UPDATE_ALERT_LIST_RESOLVED', { id, isResolved })
  },
  setAlertListLoaded({ commit }, isLoaded) {
    commit('SET_ALERT_LIST_LOADED', isLoaded)
  },
  setUnresolvedAlertCounts({ commit }, counts) {
    commit('SET_UNRESOLVED_ALERT_COUNTS', counts)
  },
  incrementUnresolvedAlertCount({ commit }, type) {
    commit('ADJUST_UNRESOLVED_ALERT_COUNT', { type, delta: 1 })
  },
}

export const getters = {
  isAlertListLoaded: (state) => state.isAlertListLoaded,
  unresolvedUrgentAlertCount: (state) => state.unresolvedAlertCounts.urgent,
  unresolvedImportantAlertCount: (state) => state.unresolvedAlertCounts.important,
}
